import {ActionIcon, Group, Stack, StackProps, Text} from "@mantine/core";
import {atIndexOrUndefined, PipelineGraphEntity} from "@ots-frontend/ots-data";
import {DateSlider} from "../../slider/DateSlider/DateSlider";
import {ReactNode, useCallback, useMemo, useState} from "react";
import {PipelineGraphView} from "../PipelineGraphView/PipelineGraphView";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {IconEye} from "@tabler/icons-react";
import {useNavigate} from "react-router-dom";

type GraphView = {
  index: number
  date: Date | number
  name: string
  data: PipelineGraphEntity
  graph: ReactNode
}

export type PipelineGraphTimelineViewProps = StackProps & {
  pipelineGraphs: PipelineGraphEntity[]
}

export const PipelineGraphTimelineView = (props: PipelineGraphTimelineViewProps) => {
  const {pipelineGraphs, ...rest} = props

  const [graphViewIndex, setGraphViewIndex] = useState<number>(pipelineGraphs.length > 0 ? pipelineGraphs.length - 1 : -1)

  const navigate = useNavigate()

  const graphViews: GraphView[] = useMemo(() => {
    const sorted = pipelineGraphs?.sort(
      (a, b) => a.effectiveDttm.valueOf() - b.effectiveDttm.valueOf()
    );

    return sorted?.map((value, index) => {
      return {
        index: index,
        date: value.effectiveDttm,
        name: value.displayableName,
        data: value,
        graph: <PipelineGraphView key={index} pipelineGraph={value}/>
      }
    })
  }, [pipelineGraphs])

  const dates = useMemo(() => {
    return graphViews.map((value) => value.date)
  }, [graphViews])

  const onDateChange = useCallback((value: number) => {
    setGraphViewIndex(value)
  }, [])

  const currGraph = useMemo(() => {
    return atIndexOrUndefined(graphViews, graphViewIndex)
  }, [graphViews, graphViewIndex])

  const onViewClick = useCallback(() => {
    if (currGraph?.data) {
      navigate(`graph?id=${currGraph.data.id}`)
    }
  }, [navigate, currGraph])

  return (
    <Stack {...rest}>
      <DateSlider pt="md" dates={dates} onChange={onDateChange}/>
      {currGraph &&
        <>
          <Text ta="left" c="dimmed" fz="xs">
            <Group spacing="xs">
              <ActionIcon color="primary" onClick={() => onViewClick()}>
                <IconEye size={16}/>
              </ActionIcon>
              <FormattedDateTime date={currGraph.date}/>
              {"(" + currGraph.name + ")"}
            </Group>
          </Text>
          {currGraph.graph}
        </>
      }
    </Stack>
  );
};
